import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../ts/Glob';
import { config } from '../../ts/config';
import SettingsImport, { ISettingsImportValue } from './AppSettingsImport';
import { ITableValue } from '../../../../_common/platform/tsx/tableSelect/Table';
import BooleanField from '../../../../_common/platform/tsx/BooleanField';

export interface IWorkspacesImport extends ISettingsImportValue {
    dirid?;
    useImportWSGUID?: boolean;
}

export default function WorkspacesImport(props: {
    value: IWorkspacesImport;
    input: string[];
    glob: IGlob;

    onChange(value: IWorkspacesImport): void;
}) {
    const [dirId, setDirId] = React.useState(props.value?.dirid);
    const [useImportWSGUID, setUseImportWSGUID] = React.useState(props.value?.useImportWSGUID);

    const selectFolderDef = React.useRef<JQuery.Deferred<any, any>>(null);

    const getValuesHash = React.useCallback(() => {
        return _.reduce(
            props.input,
            (hash, obj, i) => {
                hash[i] = obj; //{name: obj, data: {}};  //
                return hash;
            },
            {}
        );
    }, [props.input]);

    const processOnChange = React.useCallback(
        (checkedList: ITableValue, dirid, useImportWSGUID: boolean, valuesHash?) => {
            if (typeof props.onChange === 'function') {
                if (valuesHash === undefined) {
                    valuesHash = getValuesHash();
                }

                const keyList = _.chain(checkedList)
                    .map((value, key) => {
                        return valuesHash[key];
                    })
                    .compact()
                    .value();

                props.onChange({
                    keyList: keyList,
                    dirid: dirid,
                    checkedList: checkedList,
                    useImportWSGUID: useImportWSGUID
                });
            }
        },
        []
    );

    React.useEffect(() => {
        if (props.value == null) {
            processOnChange(props.value?.checkedList, dirId, useImportWSGUID);
        }

        return () => {
            if (selectFolderDef.current != null) {
                selectFolderDef.current.reject();
                selectFolderDef.current = null;
            }
        };
    }, [props.value?.checkedList, dirId, useImportWSGUID]);

    const getOnSettingsChangeHandler = (valuesHash) => {
        return (obj: ISettingsImportValue) => {
            processOnChange(obj.checkedList, dirId, valuesHash);
        };
    };

    const handleOnFolderChange = (id) => {
        processOnChange(props.value?.checkedList, id, useImportWSGUID);
        setDirId(id);
        if (id) {
            const dataDefault = props.glob.DirsStore._state.data['default'];
            let wsPathDirId = getPathId(dirId, dataDefault);
            let localStorage = JSON.stringify({ wsPathDirId: wsPathDirId, id: id });
            props.glob.AppStore.saveToLocalStorage('workspaceDirSelectedId', localStorage);
        }
    };

    const getPathId = (id, dataDefault) => {
        if (dataDefault == null || dataDefault.data == null) {
            return [];
        }

        let obj = dataDefault.data.dirsHash[id];

        const arr = [];

        while (obj != null) {
            arr.unshift(obj.dirid);
            obj = obj.parent;
        }

        return arr;
    };

    const getPath = (id, dataDefault) => {
        if (dataDefault == null || dataDefault.data == null) {
            return [];
        }

        let obj = dataDefault.data.dirsHash[id];

        const arr = [];

        while (obj != null) {
            arr.unshift(obj.name);
            obj = obj.parent;
        }

        return arr;
    };

    const handleOnFolderSelect = () => {
        const def = $.Deferred();
        const localStorage = props.glob.AppStore.getFromLocalStorage('workspaceDirSelectedId');

        let dir;
        let wsPathDirId;
        if (localStorage?.length > 0) {
            let localStorageParsed = JSON.parse(localStorage);
            dir = parseInt(localStorageParsed?.id);
            wsPathDirId = localStorageParsed?.wsPathDirId;
        }
        props.glob.DialogActions.createDialog(
            'Select destination folder for imported workspaces',
            '',
            (dirid) => {
                def.resolve(dirid);
            },
            () => {
                def.reject();
            },
            () => {
                def.reject();
            },
            'workspaceDirSelect',
            {
                selectTitle: 'OK',
                rootId: config.WIDGETS_ROOT_DIR_ID,
                overwriteShow: false,
                allowSelectItem: false,
                dir: dir,
                wsPathDirId: wsPathDirId
            }
        );

        def.done((id) => {
            handleOnFolderChange(id);
        });

        if (selectFolderDef.current != null) {
            selectFolderDef.current.reject();
        }

        selectFolderDef.current = def;
    };

    const handleOnFolderRemove = () => {
        handleOnFolderChange(null);
        handleOnFolderSelect();
    };

    const handleOnUseImportWSGUIDChange = (value: boolean) => {
        setUseImportWSGUID(value);
        processOnChange(props.value?.checkedList, dirId, value, valuesHash);
    };

    const dataDefault = props.glob.DirsStore._state.data['default'];

    let folderSelectButton: React.JSX.Element;

    if (dirId != null && dataDefault != null && dataDefault.data != null) {
        folderSelectButton = (
            <div className="btn btn-default">
                <span className="glyphicon glyphicon-remove" onClick={handleOnFolderRemove}></span>
                {'\\' + getPath(dirId, dataDefault).join('\\')}
            </div>
        );
    } else {
        folderSelectButton = (
            <div className="btn btn-default" onClick={handleOnFolderSelect}>
                <span>{'Select Target Folder'}</span>
            </div>
        );
    }

    const valuesHash = getValuesHash();

    return (
        <div className="workspaces-import">
            <div className="d-flex flex-row align-items-center">
                <div className="mr-3 file-select">
                    <div className="target-folder-select">{folderSelectButton}</div>
                </div>

                <div className="group-title use-import-ws-guid">
                    <BooleanField
                        value={useImportWSGUID}
                        title={'Use GUID to import Workspaces'}
                        onChange={handleOnUseImportWSGUIDChange}
                    />
                </div>
            </div>

            <div className="table-container">
                <SettingsImport
                    value={props.value}
                    input={props.input}
                    valuesHash={valuesHash}
                    useFullTable={true}
                    glob={props.glob}
                    onChange={getOnSettingsChangeHandler(valuesHash)}
                />
            </div>
        </div>
    );
}
